import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { FiTrendingUp, FiDollarSign, FiPackage, FiShoppingCart, FiAlertTriangle, FiX, FiCreditCard, FiZap, FiRefreshCw, FiClock, FiAward, FiSmile, FiSun, FiPlusCircle, FiMinusCircle, FiUserPlus, FiPauseCircle, FiPieChart, FiTarget, FiShare2, FiBarChart2, FiArrowRight, FiEye, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify'; // Ajout de l'import toast
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import api from '../services/api';
import { Link } from 'react-router-dom';
import DashboardContext from '../context/DashboardContext';
import AuthContext from '../context/AuthContext';
import Spinner from '../components/Spinner';
import FeatureComingSoonModal from '../components/FeatureComingSoonModal';
import PaymentModal from '../components/PaymentModal';
import ReceiptModal from '../components/ReceiptModal';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const WelcomeModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center">
    <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
      <div className="flex justify-end">
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={24} />
        </button>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-4">Bienvenue sur Jaapand-<span className="text-brand-green">e</span> !</h3>
      <p className="text-gray-600 mb-6">
        Voici quelques étapes pour bien démarrer :
      </p>
      <ul className="text-left space-y-2 mb-8">
        <li>1. Allez dans la section <Link to="/dashboard/stock" className="font-bold text-brand-blue">Gestion du Stock</Link> pour ajouter vos premiers produits.</li>
        <li>2. Utilisez la page <Link to="/dashboard/sales" className="font-bold text-brand-blue">Ventes</Link> pour enregistrer vos transactions.</li>
        <li>3. Suivez vos performances depuis ce tableau de bord.</li>
      </ul>
      <button onClick={onClose} className="bg-brand-green text-white font-bold py-2 px-6 rounded-lg">
        Compris !
      </button>
    </div>
  </div>
);

const OrdersModal = ({ isOpen, onClose, orders, onValidate, onReject }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-800">Commandes en ligne</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={24} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-grow">
          {orders.length === 0 ? (
            <p className="text-center text-gray-500">Aucune commande en attente.</p>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order._id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-gray-800">{order.customer?.name || order.customerInfo?.name || 'Client inconnu'}</p>
                      <p className="text-sm text-gray-500">{order.customer?.phone || order.customerInfo?.phone}</p>
                      <p className="text-xs text-gray-400">{new Date(order.date).toLocaleString()}</p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full">En attente</span>
                  </div>
                  <div className="border-t border-b py-2 my-2 space-y-1">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.quantity}x {item.name || 'Produit'}</span>
                        <span>{((item.salePrice || 0) * (item.quantity || 0)).toLocaleString()} F</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <p className="font-bold text-lg">Total: {(order.totalAmount || 0).toLocaleString()} F</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => onReject(order._id)}
                        className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                      >
                        Refuser
                      </button>
                      <button 
                        onClick={() => onValidate(order._id)}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors shadow-sm"
                      >
                        Valider la commande
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
        legend: { display: false },
        tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#111827', bodyColor: '#4B5563',
            borderColor: '#E5E7EB', borderWidth: 1,
            padding: 12, boxPadding: 6, usePointStyle: true,
            titleFont: { size: 13, weight: 'bold' },
            bodyFont: { size: 12 },
            callbacks: {
                label: function(context) {
                    let label = context.dataset.label || '';
                    if (label) { label += ': '; }
                    if (context.parsed.y !== null) { label += context.parsed.y.toLocaleString('fr-FR') + ' CFA'; }
                    return label;
                }
            }
        }
    },
    scales: {
        x: {
            grid: { display: false, drawBorder: false },
            ticks: { font: { size: 11 } }
        },
        y: {
            grid: { color: '#F3F4F6', borderDash: [5, 5], drawBorder: false },
            ticks: {
                font: { size: 11 },
                callback: function(value) {
                    if (value >= 1000000) return (value/1000000).toFixed(1) + 'M';
                    if (value >= 1000) return (value/1000).toFixed(0) + 'k';
                    return value;
                }
            }
        }
    }
};

const StatCard = ({ title, value, icon, colorClass, link, isClickable }) => {
    const content = (
        <div className={`bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-start gap-4 ${isClickable ? 'hover:shadow-md hover:border-gray-200 transition-all' : ''}`}>
            <div className={`p-3 rounded-xl ${colorClass}`}>
                {icon}
            </div>
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            </div>
        </div>
    );

    if (isClickable && link) {
        return <Link to={link}>{content}</Link>;
    }
    return content;
};

// Composant de Checklist de démarrage
const SetupChecklist = ({ productCount, hasSales }) => {
    // Si l'utilisateur a des produits et a déjà vendu, on ne montre pas ce guide
    if (productCount > 0 && hasSales) return null;

    return (
        <div className="mb-8 bg-gradient-to-r from-blue-600 to-brand-blue rounded-2xl p-6 text-white shadow-lg relative overflow-hidden animate-fade-in">
            <div className="absolute right-0 top-0 h-full w-1/3 bg-white/5 skew-x-12"></div>
            <div className="relative z-10">
                <h3 className="text-xl font-bold mb-4 flex items-center"><FiTarget className="mr-2"/> Bienvenue ! Commençons ensemble :</h3>
                <div className="space-y-3 max-w-2xl">
                    <div className={`flex items-center p-3 rounded-xl transition-all ${productCount > 0 ? 'bg-white/20 text-blue-100' : 'bg-white text-brand-blue shadow-sm'}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${productCount > 0 ? 'bg-white/30' : 'bg-brand-blue text-white'}`}>
                            {productCount > 0 ? <FiCheck size={14}/> : '1'}
                        </div>
                        <div className="flex-grow flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <span className={productCount > 0 ? "line-through" : "font-bold"}>Ajoutez vos premiers produits au stock</span>
                            {productCount === 0 && <Link to="/dashboard/stock" className="text-xs bg-blue-100 text-brand-blue px-4 py-2 rounded-full font-bold hover:bg-blue-200 text-center">Ajouter maintenant</Link>}
                        </div>
                    </div>
                    
                    <div className={`flex items-center p-3 rounded-xl transition-all ${hasSales ? 'bg-white/20 text-blue-100' : 'bg-white/10 text-white border border-white/20'}`}>
                         <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${hasSales ? 'bg-white/30' : 'bg-white/20'}`}>
                            {hasSales ? <FiCheck size={14}/> : '2'}
                        </div>
                        <div className="flex-grow flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <span className={hasSales ? "line-through" : "font-medium"}>Réalisez votre première vente</span>
                            {productCount > 0 && !hasSales && <Link to="/dashboard/sales" className="text-xs bg-white text-brand-blue px-4 py-2 rounded-full font-bold hover:bg-gray-100 text-center">Aller à la caisse</Link>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Dashboard = () => {
  const { dashboardData, loading, refreshDashboard } = useContext(DashboardContext);
  const { user } = useContext(AuthContext);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [insight, setInsight] = useState(null);
  const [heldSalesCount, setHeldSalesCount] = useState(0);
  const [todayExpenses, setTodayExpenses] = useState(0);
  const [dailyGoal, setDailyGoal] = useState(0);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
  const [salesChartData, setSalesChartData] = useState(null);
  
  // État pour le paiement manuel
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [paymentPrice, setPaymentPrice] = useState(0);

  const [selectedSale, setSelectedSale] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  // Filtrer les produits "Service" des alertes de stock faible
  const lowStockProducts = useMemo(() => {
    return dashboardData?.lowStockProducts?.filter(p => !p.isService) || [];
  }, [dashboardData]);

  // Vérifier les ventes en attente au chargement
  useEffect(() => {
    const checkHeldSales = () => {
      try {
        const saved = localStorage.getItem('heldSales');
        if (saved) {
          setHeldSalesCount(JSON.parse(saved).length);
        }
      } catch (e) { console.error(e); }
    };
    checkHeldSales();
  }, []);

  // Rafraîchir les données du dashboard à chaque chargement de la page pour voir les nouvelles ventes
  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);


  // Récupérer les données pour le graphique des ventes
  useEffect(() => {
    const fetchChartData = async () => {
        try {
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - 6); // 7 derniers jours

            const formatForApi = (date) => date.toISOString().split('T')[0];

            const response = await api.get('/reports/advanced', {
                params: { 
                    startDate: formatForApi(startDate), 
                    endDate: formatForApi(endDate) 
                },
            });

            if (response.data && response.data.salesOverTime) {
                const apiLabels = response.data.salesOverTime.map(d => new Date(d.date || d._id).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }));
                const apiData = response.data.salesOverTime.map(d => d.revenue || d.totalRevenue || 0);

                const fullLabels = [];
                const fullData = [];
                for (let i = 6; i >= 0; i--) {
                    const d = new Date();
                    d.setDate(d.getDate() - i);
                    const label = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                    fullLabels.push(label);

                    const dataIndex = apiLabels.indexOf(label);
                    fullData.push(dataIndex > -1 ? apiData[dataIndex] : 0);
                }

                setSalesChartData({
                    labels: fullLabels,
                    datasets: [{
                        label: 'Chiffre d\'affaires',
                        data: fullData,
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        tension: 0.4,
                        fill: true,
                    }],
                });
            }
        } catch (error) {
            console.error("Erreur chargement données du graphique", error);
        }
    };
    if (user && (user.plan === 'pro' || user.plan === 'premium')) fetchChartData();
  }, [user]);


  // Fonction optimisée pour récupérer les commandes en attente
  const fetchPendingOrders = useCallback(async () => {
    if (!navigator.onLine) return; // STOP : Ne pas chercher si hors ligne

    try {
      const response = await api.get('/sales');
      let allSales = [];

      // Gestion robuste de la réponse (tableau ou pagination)
      if (Array.isArray(response.data)) {
        allSales = response.data;
      } else if (response.data && Array.isArray(response.data.sales)) {
        allSales = response.data.sales;
      } else if (response.data && Array.isArray(response.data.data)) {
        allSales = response.data.data;
      }

      if (allSales) {
        // Filtrage plus large pour capturer différents statuts possibles (insensible à la casse)
        const orders = allSales.filter(s => {
            const status = s.status ? s.status.toLowerCase() : '';
            return ['pending', 'en attente', 'commande en ligne', 'order_placed', 'created', 'pending_confirmation'].includes(status);
        });
        // Tri par date décroissante (la plus récente en premier)
        orders.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPendingOrders(orders);
      }
    } catch (error) {
      console.error("Erreur chargement commandes en ligne", error);
    }
  }, []);

  // Récupérer les dépenses du jour et les données du graphique
  useEffect(() => {
    const fetchExtraData = async () => {
        try {
            const now = new Date();
            const offset = now.getTimezoneOffset();
            const todayDate = new Date(now.getTime() - (offset * 60 * 1000));
            const todayStr = todayDate.toISOString().split('T')[0];
            
            // 1. Dépenses du jour
            const expensesRes = await api.get('/expenses', { params: { startDate: todayStr, endDate: todayStr } });
            
            const startObj = new Date(todayStr); startObj.setHours(0,0,0,0);
            const endObj = new Date(todayStr); endObj.setHours(23,59,59,999);
            
            const filteredExpenses = expensesRes.data.filter(exp => {
                const expDate = new Date(exp.date);
                return expDate >= startObj && expDate <= endObj;
            });
            const totalExpenses = filteredExpenses.reduce((acc, curr) => acc + curr.amount, 0);
            setTodayExpenses(totalExpenses);

        } catch (error) {
            console.error("Erreur chargement données supplémentaires dashboard", error);
        }
    };
    if (user) fetchExtraData();
  }, [user]);

  // Récupérer les commandes en ligne en attente
  useEffect(() => {
    if (user) {
        fetchPendingOrders();
        const interval = setInterval(fetchPendingOrders, 15000); // Vérification auto toutes les 15s
        return () => clearInterval(interval);
    }
  }, [user, fetchPendingOrders]);

  // Logique pour le tableau de bord intelligent
  useEffect(() => {
    if (dashboardData) {
      const fetchInsightData = async () => {
        try {
          // Utiliser la date locale pour éviter les décalages UTC qui faussent "aujourd'hui"
          const now = new Date();
          const offset = now.getTimezoneOffset();
          const todayDate = new Date(now.getTime() - (offset * 60 * 1000));
          const todayStr = todayDate.toISOString().split('T')[0];
          const dayOfMonth = now.getDate();
          const currentHour = now.getHours();

          const yesterday = new Date(todayDate);
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayStr = yesterday.toISOString().split('T')[0];

          // Récupération parallèle des données comparatives
          const [yesterdayRes, expensesRes] = await Promise.all([
            api.get('/reports/summary', { params: { startDate: yesterdayStr, endDate: yesterdayStr } }),
            api.get('/expenses', { params: { startDate: todayStr, endDate: todayStr } })
          ]);

          const yesterdaySales = yesterdayRes.data.revenue || 0;
          
          // Filtrage côté client pour s'assurer que seules les dépenses du jour sont comptées
          const startObj = new Date(todayStr); startObj.setHours(0,0,0,0);
          const endObj = new Date(todayStr); endObj.setHours(23,59,59,999);
          
          const filteredExpenses = expensesRes.data.filter(exp => {
              const expDate = new Date(exp.date);
              return expDate >= startObj && expDate <= endObj;
          });
          const todayExpenses = filteredExpenses.reduce((acc, curr) => acc + curr.amount, 0);
          const todaySales = dashboardData.dailySales || 0;
          const lowStockCount = dashboardData.lowStockProducts?.filter(p => !p.isService).length || 0;
          const overdueDebtsCount = dashboardData.overdueDebts || 0;
          const userName = user?.username || 'Patron';

          // Assistant : Objectif du jour (Ventes d'hier + 10%, ou minimum 50.000F pour démarrer)
          const calculatedGoal = Math.max((yesterdaySales * 1.1), 50000);
          setDailyGoal(calculatedGoal);

          let message = '';
          let type = 'info'; // info, warning, success
          let action = null;

          // Logique de priorité pour l'assistant virtuel
          if (todayExpenses > todaySales && todaySales > 0) {
            message = `Attention ${userName}, vos dépenses (${todayExpenses.toLocaleString('fr-FR')} F) dépassent vos recettes. Surveillez la caisse ! 📉`;
            type = 'warning';
            action = { label: 'Voir dépenses', link: '/dashboard/expenses' };
          } else if (dayOfMonth >= 25 && overdueDebtsCount > 0) {
             // Assistant : Fin du mois = Recouvrement
             message = `C'est la fin du mois ${userName}. Pensez à relancer vos ${overdueDebtsCount} clients débiteurs pour sécuriser la trésorerie. 🗓️`;
             type = 'warning';
             action = { label: 'Voir créances', link: '/dashboard/debts' };
          } else if (todaySales > yesterdaySales * 1.15 && yesterdaySales > 0) {
            const percent = ((todaySales - yesterdaySales) / yesterdaySales * 100).toFixed(0);
            message = `Incroyable ${userName} ! 🚀 +${percent}% par rapport à hier. Vous êtes en feu aujourd'hui !`;
            type = 'success';
          } else if (lowStockCount > 0 && Math.random() > 0.5) {
             message = `Conseil de gestion : Vous avez ${lowStockCount} produits bientôt en rupture. Un petit réassort pour éviter de perdre des clients ? 📦`;
             type = 'warning';
             action = { label: 'Voir stock', link: '/dashboard/stock?filter=low_stock' };
          } else if (overdueDebtsCount > 0 && Math.random() > 0.5) {
             message = `L'argent dehors, c'est votre bénéfice. Vous avez ${overdueDebtsCount} dettes en retard. Un petit SMS de relance ? 📱`;
             type = 'info';
             action = { label: 'Relancer', link: '/dashboard/debts' };
          } else if (todaySales === 0) {
             if (currentHour < 11) {
                 message = `Bonjour ${userName} ! Tout est calme pour l'instant. Profitez-en pour vérifier vos stocks ou préparer vos commandes. ☕`;
                 type = 'info';
             } else {
                 message = `C'est calme ${userName}... Peut-être une petite promo WhatsApp pour réveiller les clients ? 📢`;
                 type = 'tip';
             }
          } else {
            // Message par défaut positif avec rotation de conseils
            const tips = [
                "Le secret de la réussite ? La régularité. Bravo pour votre gestion.",
                "Un client satisfait est un client qui revient. Avez-vous fidélisé quelqu'un aujourd'hui ?",
                "Avez-vous pensé à vérifier vos marges sur les produits les plus vendus ?",
                "Votre boutique tourne bien. C'est le moment idéal pour analyser vos rapports."
            ];
            const randomTip = tips[Math.floor(Math.random() * tips.length)];
            
            // Salutation temporelle
            let greeting = "Bonjour";
            if (currentHour >= 18) greeting = "Bonsoir";
            else if (currentHour >= 12 && currentHour < 14) greeting = "Bon appétit";

            message = `${greeting} ${userName}. ${todaySales.toLocaleString('fr-FR')} CFA de ventes aujourd'hui. ${randomTip}`;
            type = 'success';
          }
          setInsight({ message, type, action });
        } catch (err) {
          console.error("Erreur insight", err);
        }
      };
      fetchInsightData();
    }
  }, [dashboardData, user]);

  const handleInitiatePayment = async (planName, price, billingCycle = 'monthly') => {
    // Ouvre le modal de paiement manuel au lieu de PayTech
    setPaymentPlan(planName);
    setPaymentPrice(price);
    setIsPaymentModalOpen(true);
  };

  // Gérer la fermeture du modal de paiement
  const handlePaymentModalClose = () => {
    setIsPaymentModalOpen(false);
  };

  const handleValidateOrder = async (orderId) => {
    try {
      await api.put(`/sales/${orderId}`, { status: 'completed' }); // Ou 'Payé' selon votre logique backend
      toast.success("Commande validée et stock mis à jour !");
      setPendingOrders(prev => prev.filter(o => o._id !== orderId));
      handleManualRefresh(); // On met à jour les stats (CA, Stock) seulement maintenant
    } catch (error) {
      toast.error("Erreur lors de la validation de la commande.");
    }
  };

  const handleRejectOrder = async (orderId) => {
    if(!window.confirm("Voulez-vous vraiment refuser cette commande ?")) return;
    try {
      await api.put(`/sales/${orderId}`, { status: 'cancelled' }); 
      toast.info("Commande refusée.");
      setPendingOrders(prev => prev.filter(o => o._id !== orderId));
    } catch (error) {
      toast.error("Erreur lors du refus de la commande.");
    }
  };

  const handleManualRefresh = () => {
    refreshDashboard();
    fetchPendingOrders();
    toast.info("Données actualisées");
  };

  const handleSharePerformance = () => {
    const shopName = user?.shopName || 'Ma Boutique';
    const sales = (dashboardData.dailySales || 0).toLocaleString('fr-FR');
    const currentPercent = dailyGoal > 0 ? ((dashboardData.dailySales || 0) / dailyGoal * 100).toFixed(0) : 0;

    const message = `🚀 *Bilan du Jour chez ${shopName}* %0A%0A` +
                    `✅ Ventes : *${sales} CFA*%0A` +
                    `🎯 Objectif : ${currentPercent}% atteint !%0A%0A` +
                    `Merci de votre confiance ! 🙏%0A` +
                    `_Géré avec Jaapand-e_`;
    
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setIsReceiptModalOpen(true);
  };

  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  const handleCloseWelcome = () => {
    localStorage.setItem('hasSeenWelcome', 'true');
    setShowWelcome(false);
  };

  const isProOrPremium = user?.plan === 'pro' || user?.plan === 'premium';

  if (loading) {
    return <Spinner message="Chargement du tableau de bord..." />;
  }

  if (!dashboardData) {
    return <div className="text-center text-red-500">Impossible de charger les données du tableau de bord.</div>;
  }

  const goalProgress = dailyGoal > 0 ? Math.min(((dashboardData.dailySales || 0) / dailyGoal) * 100, 100) : 0;
  const goalPercentage = dailyGoal > 0 ? ((dashboardData.dailySales || 0) / dailyGoal * 100).toFixed(0) : 0;
  
  // Déterminer si l'utilisateur a déjà des produits et des ventes (pour l'affichage du guide)
  const productCount = dashboardData.productUsage?.count || 0;
  const hasSales = (dashboardData.recentSales?.length > 0) || (dashboardData.dailySales > 0);

  return (
    <div id="tour-step-welcome">
      <OrdersModal isOpen={isOrdersModalOpen} onClose={() => setIsOrdersModalOpen(false)} orders={pendingOrders} onValidate={handleValidateOrder} onReject={handleRejectOrder} />
      <PaymentModal 
        isOpen={isPaymentModalOpen} 
        onClose={handlePaymentModalClose} 
        planName={paymentPlan} 
        price={paymentPrice} 
      />
      {showWelcome && <WelcomeModal onClose={handleCloseWelcome} />}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
        <div>
            <h2 className="text-3xl font-bold text-gray-900 font-poppins">Bonjour, {user?.username || 'Patron'} ! 👋</h2>
            <p className="text-gray-500 mt-1">Voici le résumé de votre activité aujourd'hui.</p>
        </div>
        <button onClick={handleManualRefresh} className="mt-4 sm:mt-0 flex items-center gap-2 bg-white text-gray-700 font-semibold py-2 px-4 rounded-xl shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors" title="Actualiser">
            <FiRefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
        </button>
      </div>

      {/* Guide de démarrage (S'affiche seulement si nécessaire) */}
      <SetupChecklist productCount={productCount} hasSales={hasSales} />
      
      {/* Assistant Intelligent */}
      {insight && (
        <div className={`mb-8 p-6 rounded-2xl shadow-lg flex items-start gap-5 relative overflow-hidden animate-fade-in ${
            insight.type === 'warning' ? 'bg-red-50 border-red-500 text-red-700' :
            insight.type === 'success' ? 'bg-green-50 border-green-500 text-green-700' :
            insight.type === 'tip' ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
            'bg-blue-50 border-blue-500 text-blue-700'
        }`}>
            <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-white/60">
                {insight.type === 'warning' ? <FiAlertTriangle size={24} /> : 
                 insight.type === 'success' ? <FiTrendingUp size={24} /> : 
                 insight.type === 'tip' ? <FiSun size={24} /> :
                 <FiSmile size={24} />}
            </div>
            <div className="flex-grow">
                <h3 className="font-bold text-lg">Assistant Jaapand-e</h3>
                <p className="mt-1">{insight.message}</p>
                {insight.action && (
                    <Link to={insight.action.link} className="mt-3 inline-block bg-white px-4 py-1.5 rounded-lg text-sm font-bold transition-colors shadow-sm hover:bg-gray-200">
                        {insight.action.label}
                    </Link>
                )}
            </div>
        </div>
      )}

      {/* Centre d'actions */}
      {(pendingOrders.length > 0 || heldSalesCount > 0 || (dashboardData.lowStockProducts?.length || 0) > 0 || dashboardData.overdueDebts > 0) && (
        <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Centre d'actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingOrders.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-2xl flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4"><FiShoppingCart size={20} /></div>
                            <div>
                                <p className="font-bold text-blue-800">Commandes en ligne</p>
                                <p className="text-sm text-blue-700">{pendingOrders.length} commande(s) en attente.</p>
                            </div>
                        </div>
                        <button onClick={() => setIsOrdersModalOpen(true)} className="text-sm font-bold text-blue-700 hover:underline whitespace-nowrap">Voir &rarr;</button>
                    </div>
                )}
                {heldSalesCount > 0 && (
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-2xl flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4"><FiPauseCircle size={20} /></div>
                            <div>
                                <p className="font-bold text-orange-800">Ventes en attente</p>
                                <p className="text-sm text-orange-700">{heldSalesCount} panier(s) à reprendre.</p>
                            </div>
                        </div>
                        <Link to="/dashboard/sales" className="text-sm font-bold text-orange-700 hover:underline whitespace-nowrap">Reprendre &rarr;</Link>
                    </div>
                )}
                {lowStockProducts.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-2xl flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4"><FiAlertTriangle size={20} /></div>
                            <div>
                                <p className="font-bold text-yellow-800">Stock faible</p>
                                <p className="text-sm text-yellow-700">{lowStockProducts.length} produit(s) à réapprovisionner.</p>
                            </div>
                        </div>
                        <Link to="/dashboard/stock?filter=low_stock" className="text-sm font-bold text-yellow-700 hover:underline whitespace-nowrap">Voir &rarr;</Link>
                    </div>
                )}
                {dashboardData.overdueDebts > 0 && (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-2xl flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4"><FiClock size={20} /></div>
                            <div>
                                <p className="font-bold text-red-800">Dettes en retard</p>
                                <p className="text-sm text-red-700">{dashboardData.overdueDebts} dette(s) client à relancer.</p>
                            </div>
                        </div>
                        <Link to="/dashboard/debts" className="text-sm font-bold text-red-700 hover:underline whitespace-nowrap">Relancer &rarr;</Link>
                    </div>
                )}
            </div>
        </div>
      )}

      <div id="tour-step-stats" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Ventes du jour"
          value={`${(dashboardData.dailySales || 0).toLocaleString('fr-FR')} CFA`}
          icon={<FiTrendingUp size={24} />}
          colorClass="bg-green-100 text-brand-green"
          link={`/dashboard/reports?filter=today`}
          isClickable={isProOrPremium}
        />
        
        <StatCard
          title="Dépenses du jour"
          value={`${todayExpenses.toLocaleString('fr-FR')} CFA`}
          icon={<FiMinusCircle size={24} />}
          colorClass="bg-red-100 text-red-500"
          link="/dashboard/expenses"
          isClickable={true}
        />

        <StatCard
          title="Bénéfice Net (Jour)"
          value={`${((dashboardData.estimatedProfit || 0) - todayExpenses).toLocaleString('fr-FR')} CFA`}
          icon={<FiPieChart size={24} />}
          colorClass="bg-indigo-100 text-indigo-500"
        />

        <StatCard
          title="Marge Brute Estimée"
          value={`${(dashboardData.estimatedProfit || 0).toLocaleString('fr-FR')} CFA`}
          icon={<FiDollarSign size={24} />}
          colorClass="bg-blue-100 text-brand-blue"
        />

        <StatCard
          title="Stock faible"
          value={lowStockProducts.length}
          icon={<FiPackage size={24} />}
          colorClass="bg-orange-100 text-orange-500"
          link="/dashboard/stock?filter=low_stock"
          isClickable={isProOrPremium}
        />

        {isProOrPremium && (
            <StatCard
                title="Total Dettes Clients"
                value={`${(dashboardData.totalDebt || 0).toLocaleString('fr-FR')} CFA`}
                icon={<FiCreditCard size={24} />}
                colorClass="bg-yellow-100 text-yellow-500"
                link="/dashboard/debts"
                isClickable={true}
            />
        )}
        {user?.plan === 'essentiel' && (
          <div className="bg-white p-5 rounded-2xl shadow-sm flex flex-col items-center justify-center text-center h-full border-2 border-dashed border-yellow-400">
            <FiZap className="h-8 w-8 text-yellow-500 mb-2" />
            <h4 className="font-bold text-gray-800">Passez au plan Pro</h4>
            <p className="text-xs text-gray-500 mt-1 mb-3">
              Suivez vos dettes clients et envoyez des rappels par SMS.
            </p>
            <button onClick={() => handleInitiatePayment('pro', 7500)} disabled={paymentLoading} className="bg-brand-green text-white font-bold py-2 px-4 rounded-xl text-sm hover:bg-green-600 transition-colors disabled:opacity-50">
              Mettre à niveau
            </button>
          </div>
        )}
      </div>

      {/* Graphique et Objectifs */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {user?.plan === 'premium' ? (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center"><FiBarChart2 className="mr-2"/> Ventes des 7 derniers jours</h3>
                <div className="h-64 flex items-center justify-center w-full">
                    {salesChartData && <Line data={salesChartData} options={lineChartOptions} />}
                </div>
            </div>
        ) : (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2 flex flex-col justify-center items-center text-center">
                <div className="p-4 bg-purple-50 rounded-full mb-4"><FiBarChart2 className="h-8 w-8 text-purple-600"/></div>
                <h4 className="font-bold text-gray-800">Évolution des ventes</h4>
                <p className="text-sm text-gray-500 mt-1 mb-4 max-w-xs">Passez au plan Premium pour visualiser vos performances sur des graphiques interactifs.</p>
                <button onClick={() => handleInitiatePayment('premium', 25000)} disabled={paymentLoading} className="bg-purple-600 text-white font-bold py-2 px-4 rounded-xl text-sm hover:bg-purple-700 transition-colors disabled:opacity-50">
                    Passer Premium
                </button>
            </div>
        )}

        <div className="lg:col-span-1 space-y-6">
            {/* Assistant : Coach de Vente (Objectif du jour) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative flex flex-col justify-between">
                <div>
                    <button
                        onClick={handleSharePerformance}
                        className="absolute top-3 right-3 text-gray-400 hover:text-green-500 transition-colors p-2 rounded-full hover:bg-green-50"
                        title="Partager mes résultats sur WhatsApp"
                    >
                        <FiShare2 size={18} />
                    </button>
                    <h3 className="text-xs font-bold text-gray-500 uppercase flex items-center tracking-wider"><FiTarget className="mr-2 text-brand-blue"/> Objectif du jour</h3>
                    <div className="flex justify-between items-center mt-2">
                        <p className="text-3xl font-extrabold text-gray-800">{(dashboardData.dailySales || 0).toLocaleString('fr-FR')} <span className="text-base text-gray-400 font-normal">/ {dailyGoal.toLocaleString('fr-FR')} F</span></p>
                        <span className={`text-2xl font-bold ${goalPercentage >= 100 ? 'text-green-600' : 'text-brand-blue'}`}>{goalPercentage}%</span>
                    </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden mt-3">
                    <div className={`h-3 rounded-full transition-all duration-1000 ease-out ${goalPercentage >= 100 ? 'bg-green-500' : 'bg-brand-blue'}`} style={{ width: `${goalProgress}%` }}></div>
                </div>
            </div>

            {/* Suivi du Quota Produits */}
            {dashboardData?.productUsage && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative">
                    <h3 className="text-xs font-bold text-gray-500 uppercase flex items-center tracking-wider"><FiPackage className="mr-2 text-brand-blue"/> Stock ({dashboardData.productUsage.plan})</h3>
                    <div className="flex justify-between items-center mt-2">
                        {dashboardData.productUsage.percentage === null ? (
                            <p className="text-2xl font-extrabold text-gray-800">{dashboardData.productUsage.count} <span className="text-sm text-gray-400 font-normal">Produits</span></p>
                        ) : (
                            <p className="text-2xl font-extrabold text-gray-800">{dashboardData.productUsage.count} <span className="text-sm text-gray-400 font-normal">/ {dashboardData.productUsage.limit}</span></p>
                        )}
                        {dashboardData.productUsage.percentage !== null && (
                            <span className={`text-xl font-bold ${dashboardData.productUsage.isLimitReached ? 'text-red-600' : 'text-brand-blue'}`}>{dashboardData.productUsage.percentage}%</span>
                        )}
                    </div>
                    {dashboardData.productUsage.percentage !== null && (
                        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden mt-3">
                            <div className={`h-2.5 rounded-full transition-all duration-1000 ease-out ${dashboardData.productUsage.isLimitReached ? 'bg-red-500' : 'bg-brand-blue'}`} style={{ width: `${dashboardData.productUsage.percentage}%` }}></div>
                        </div>
                    )}
                </div>
            )}
            
            {/* Section Top Produits du Jour */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-base font-semibold text-gray-700 mb-4 flex items-center">
                <FiAward className="mr-2 text-yellow-500" /> Top Produits du Jour
                </h3>
                <div className="space-y-4">
                {dashboardData.topProductsToday?.length > 0 ? (
                    dashboardData.topProductsToday.map((product, index) => {
                    const maxQuantity = dashboardData.topProductsToday[0].totalQuantity;
                    const percentage = maxQuantity > 0 ? (product.totalQuantity / maxQuantity) * 100 : 0;
                    return (
                        <div key={product._id} className="group">
                            <div className="flex justify-between items-center mb-1.5 text-sm">
                                <div className="flex items-center">
                                    <span className="w-6 h-6 flex-shrink-0 mr-3 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center group-hover:bg-yellow-100 group-hover:text-yellow-600 transition-colors">{index + 1}</span>
                                    <span className="font-bold text-gray-800">{product.name}</span>
                                </div>
                                <span className="font-semibold text-gray-600">{product.totalQuantity} vendus</span>
                            </div>
                            <div className="bg-gray-100 rounded-full h-2 ml-9"><div className="bg-brand-green h-2 rounded-full" style={{ width: `${percentage}%` }}></div></div>
                        </div>
                    )
                    })
                ) : (
                    <p className="text-center text-gray-500 py-4">Aucune vente enregistrée aujourd'hui.</p>
                )}
                </div>
            </div>
        </div>
      </div>

      {/* Raccourcis Rapides */}
      <div id="tour-step-actions" className="mt-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Accès Rapide</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/dashboard/sales" className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all transform hover:-translate-y-1 flex flex-col items-center justify-center text-center border border-gray-100 group">
            <div className="bg-blue-50 p-4 rounded-full mb-4 group-hover:bg-brand-blue group-hover:text-white transition-colors duration-300">
              <FiShoppingCart className="text-brand-blue h-6 w-6 group-hover:text-white" />
            </div>
            <span className="font-bold text-gray-700 text-sm group-hover:text-brand-blue transition-colors">Nouvelle Vente</span>
          </Link>
          <Link to="/dashboard/expenses" className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all transform hover:-translate-y-1 flex flex-col items-center justify-center text-center border border-gray-100 group">
            <div className="bg-red-50 p-4 rounded-full mb-4 group-hover:bg-red-500 group-hover:text-white transition-colors duration-300">
              <FiMinusCircle className="text-red-500 h-6 w-6 group-hover:text-white" />
            </div>
            <span className="font-bold text-gray-700 text-sm group-hover:text-red-500 transition-colors">Ajouter Dépense</span>
          </Link>
          <Link to="/dashboard/stock" className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all transform hover:-translate-y-1 flex flex-col items-center justify-center text-center border border-gray-100 group">
            <div className="bg-green-50 p-4 rounded-full mb-4 group-hover:bg-brand-green group-hover:text-white transition-colors duration-300">
              <FiPlusCircle className="text-brand-green h-6 w-6 group-hover:text-white" />
            </div>
            <span className="font-bold text-gray-700 text-sm group-hover:text-brand-green transition-colors">Ajouter Produit</span>
          </Link>
          <Link to="/dashboard/clients" className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all transform hover:-translate-y-1 flex flex-col items-center justify-center text-center border border-gray-100 group">
            <div className="bg-purple-50 p-4 rounded-full mb-4 group-hover:bg-purple-500 group-hover:text-white transition-colors duration-300">
              <FiUserPlus className="text-purple-500 h-6 w-6 group-hover:text-white" />
            </div>
            <span className="font-bold text-gray-700 text-sm group-hover:text-purple-500 transition-colors">Nouveau Client</span>
          </Link>
        </div>
      </div>

      {/* Ventes récentes */}
      <div className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
            <h3 className="text-lg font-bold text-gray-800 flex items-center"><FiShoppingCart className="mr-2 text-brand-blue" /> Ventes Récentes</h3>
            <Link to="/dashboard/reports" className="text-sm font-semibold text-brand-blue hover:text-blue-700 flex items-center">Tout voir <FiArrowRight className="ml-1"/></Link>
        </div>
        <div className="divide-y divide-gray-50">
          {dashboardData.recentSales?.length > 0 ? (
            dashboardData.recentSales.map(sale => (
              <div key={sale._id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-blue-50 text-brand-blue flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                        <FiShoppingCart size={18} />
                    </div>
                    <div>
                        <p className="font-bold text-gray-900">{(sale.totalAmount || 0).toLocaleString('fr-FR')} CFA</p>
                        <p className="text-xs text-gray-500 flex items-center mt-0.5">
                            <FiClock className="mr-1" size={10} /> {new Date(sale.date).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} • {sale.items.length} article{sale.items.length > 1 ? 's' : ''}
                        </p>
                    </div>
                  </div>
                  <button onClick={() => handleViewSale(sale)} className="text-gray-300 hover:text-brand-blue p-2 rounded-full hover:bg-blue-50 transition-colors" title="Voir le ticket">
                    <FiEye size={18} />
                  </button>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 py-8">Aucune vente récente.</p>
          )}
        </div>
      </div>
      <ReceiptModal 
        isOpen={isReceiptModalOpen} 
        onClose={() => setIsReceiptModalOpen(false)} 
        sale={selectedSale} 
      />
    </div>
  );
};

export default Dashboard;